import { Colors, Metrics, Helpers, Heading3 } from '@origin-digital/style-guide';
import React from 'react';
import styled from 'styled-components';
import { Savings } from '../../CalculatorResult.types';
// @ts-ignore
import calculatorMetaData from '../../../../calculatorMetaData.json';
import { ClickableUnderLineLink } from '../../../../../../components/Link/ClickableLink';
import { formatAsCurrency } from '../../../../../../utils/number/numberHelper';

const SavingsBreakdownStyles = styled.div`
  p {
    font-size: ${Metrics.FontSizes.BODY}px;
    color: ${Colors.GREY};
    line-height: ${Metrics.LineHeights.BODY}px;

    strong {
      color: ${Colors.GREY56};
    }
  }
`;

const Breakdown = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-bottom: 1px solid ${Colors.GREY16};
  margin-top: ${Metrics.Spacing.M_Y}px;

  span.price {
    padding-right: ${Metrics.Spacing.M_X_LG}px;
  }

  * {
    border-top: 1px solid ${Colors.GREY16};
    padding: ${Metrics.Spacing.M_Y_SM}px 0;
    font-size: ${Metrics.FontSizes.BODY}px;
  }

  .label {
    flex-basis: 100%;
  }
  .consumed {
    flex: 1 1 33%;
  }
  .rate {
    text-align: left;
    flex: 1 1 36%;
  }
  .price {
    flex: 1 1 30%;
  }

  .total {
    flex: 1 1 40%;
  }

  strong:not(.total) {
    text-align: right;
  }

  ${Helpers.media.MD`
    .label {
      flex: 1 1 30%;
      padding-right: 10px;
    }
    .consumed, .rate, .price {
      flex-basis: 20%;
    }
    .total {
      flex: 1 1 60%;
    }
  `};
`;

export interface SavingsBreakdownProps {
  savings: Savings;
  onShowDisclaimer: () => void;
}

function getTextForUsage(usagePercentDuringDay: any | null): string | null {
  const usageValue = `${usagePercentDuringDay * 100}`;
  const foundOption = calculatorMetaData.form.fields.usagePercentDuringDay.options.find(
    (option: any) => option.value === usageValue
  );
  if (foundOption && foundOption.label) {
    return foundOption.label.toLowerCase();
  }
  return null;
}

function getMessageForUsage(savings: Savings) {
  const textForUsage = getTextForUsage(savings.usagePercentDuringDay);
  if (textForUsage) {
    return (
      <span>
        , with most of your energy used
        <strong data-id="usageBreakdown"> {textForUsage}</strong>
      </span>
    );
  }
  return null;
}

export const SavingsBreakdown: React.SFC<SavingsBreakdownProps> = ({ savings, onShowDisclaimer }) => (
  <SavingsBreakdownStyles>
    <Heading3 text={'How was this worked out?'} />
    <p>
      We estimate a potential savings of
      <strong data-id="billSavingsForPeriodBreakdown" style={{ color: Colors.GREEN }}>
        {` ${formatAsCurrency(savings.billSavingsForPeriod)} `}
      </strong>
      based on:
    </p>

    <p>
      Your average quarterly bill of
      <strong data-id="billCostForPeriodBreakdown">{` ${formatAsCurrency(savings.billCostForPeriod)}`}</strong>
      {getMessageForUsage(savings)}, which means you
      {"'"}d likely use
      <strong data-id="energyConsumedDuringPeriodBreakdown"> {savings.solarEnergyConsumedKwh} kWh </strong>
      of the solar energy you generate.
    </p>

    <ClickableUnderLineLink text={'Other assumptions we made'} onClick={onShowDisclaimer} />

    <Breakdown>
      <span className="label">Solar energy used in your home</span>
      <span data-id="energyConsumedKwhBreakdown" className="consumed">
        {`${Math.round(savings.solarEnergyConsumedKwh!)} kWh`}
      </span>
      <span data-id="energyConsumptionRateCentsBreakdown" className="rate">
        x {savings.solarEnergyConsumptionRateCents}c / kWh
      </span>
      <strong data-id="energyConsumedDollarsBreakdown" className="price">
        {`${formatAsCurrency(savings.solarEnergyConsumedDollars)}`}
      </strong>
      <span className="label">Solar energy fed back into the grid</span>
      <span data-id="energySoldKwhBreakdown" className="consumed">
        {`${Math.round(savings.solarEnergySoldKwh!)} kWh`}
      </span>
      <span data-id="energySoldRateCentsBreakdown" className="rate">
        x {savings.solarEnergySoldRateCents}c / kWh
      </span>
      <strong data-id="energySoldDollarsBreakdown" className="price">
        {`${formatAsCurrency(savings.solarEnergySoldDollars)}`}
      </strong>
      <strong className="total">Total saved</strong>
      <strong data-id="billSavingsForPeriodBreakdownTotal" className="price">
        {`${formatAsCurrency(savings.billSavingsForPeriod)}`}
      </strong>
    </Breakdown>
  </SavingsBreakdownStyles>
);
